import React from 'react';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import FraudFooter from 'fraud/components/fraud_footer';
import FraudSignalsList from 'fraud/components/fraud_signals';
import FraudResolvedSignalsInfo from 'fraud/components/fraud_resolved_signals_info';
import FraudStatusBanner from 'fraud/components/fraud_status_banner';
import FraudStatusBannerActions from 'fraud/components/fraud_status_actions';
import { useFraudReportStatus } from 'fraud/hooks/useFraudReportStatus';
import { FraudReport } from 'fraud/types';
import styled from 'styled-components';

const FraudReportWrapper = styled.div`
  ul {
    list-style-type: disc;
    padding-left: ${Tokens.spacing['3']};
  }

  p {
    font-size: 14px;
    line-height: 1.25rem;
  }

  [data-provides='fraud-status-banner'] {
    p:not(:first-child),
    li {
      font-size: 12px;
      line-height: 1.25rem;
    }
  }

  [data-provides='fraud-footer'] {
    p,
    a {
      font-size: 14px;
      line-height: 1.25rem;
    }
  }

  &&& [data-provides='learn-about-fraud-detection-link'] {
    font-size: 14px;
    line-height: 1.25rem;
  }
`;

const FraudReport = ({
  canRunFraudReports,
  fraudReport,
  isPending,
  openFullReportModal,
  onRunFraudReport,
  onRetryFailedFraudReport,
  onRestoreFraudReport,
  onResolveFraudReport,
  onRejectAsSecurityConcern,
  canReject = false,
  canRejectAsSecurityConcern,
}: {
  canRunFraudReports: boolean;
  fraudReport: FraudReport | null;
  isPending: boolean;
  openFullReportModal: () => void;
  onRunFraudReport: () => void;
  onRetryFailedFraudReport: () => void;
  onRestoreFraudReport?: () => void;
  onResolveFraudReport?: () => void;
  onRejectAsSecurityConcern?: () => void;
  canReject?: boolean;
  canRejectAsSecurityConcern: boolean;
}) => {
  // This `isPending` boolean is from the parent component when fetching the fraud report.
  const showStatusBanner = !isPending;
  const { isReportResolved } = useFraudReportStatus(fraudReport);

  return (
    <FraudReportWrapper>
      <Grid
        container
        direction="column"
        spacing={0}
        sx={{
          paddingTop: 0,
          paddingLeft: 4,
          paddingBottom: 1.5,
          paddingRight: 3,
        }}
        data-provides="fraud-report"
      >
        <Stack spacing={2} sx={{ width: '100%' }}>
          {showStatusBanner && <FraudStatusBanner fraudReport={fraudReport} />}
          <FraudSignalsList
            positiveSignals={fraudReport?.positive_signals || []}
            fraudSignals={fraudReport?.fraud_signals || []}
            showFraudSignals={!isReportResolved}
          />
          {isReportResolved && fraudReport && (
            <FraudResolvedSignalsInfo
              dismissedBy={fraudReport.resolved_by}
              signalCount={fraudReport.fraud_signals.length}
              date={fraudReport.resolved_at}
              resolvedReasons={fraudReport.resolved_reasons}
            />
          )}
          <FraudStatusBannerActions
            canRunFraudReports={canRunFraudReports}
            fraudReport={fraudReport}
            openFullReportModal={openFullReportModal}
            onRunFraudReport={onRunFraudReport}
            onRetryFailedFraudReport={onRetryFailedFraudReport}
            onRestoreFraudReport={onRestoreFraudReport}
            onResolveFraudReport={onResolveFraudReport}
            onRejectAsSecurityConcern={onRejectAsSecurityConcern}
            canReject={canReject}
            canRejectAsSecurityConcern={canRejectAsSecurityConcern}
          />
          <FraudFooter />
        </Stack>
        <Grid
          sx={{
            paddingY: 1.5,
            paddingX: 0,
          }}
        >
          <Divider />
        </Grid>
      </Grid>
    </FraudReportWrapper>
  );
};

export default FraudReport;
